import { Alert, Badge, Button, Card, List, ListRow } from '@platform/ui';
import { Link } from '@tanstack/react-router';

import { useMessages } from '../i18n';
import type { SetupStep } from '../lib/api';
import { useDismissSetup, useSetup } from '../lib/dashboard';
import { problemText } from '../lib/problems';
import { useCanOpen, type PagePath } from '../lib/session';

/** The page that completes each step, by the step's key in `GET /v1/setup`. */
const STEP_PAGES = {
    channel: '/kanallar',
    products: '/urunler',
    mapping: '/kategori-eslestirme',
    carrier: '/kargo',
    invoice: '/faturalar/ayarlar',
    shop: '/magaza',
} as const satisfies Record<SetupStep['key'], PagePath>;

/**
 * The first steps that make the account work, on Genel Bakış (docs/11 §4).
 *
 * The API answers each person the steps their role may do and the count of
 * those, so a warehouse employee is not told to connect an e-invoice service.
 * A step names the page that completes it and links there only when the
 * session may open it, as every other link on this screen does.
 *
 * The card is help, not news: while it loads, when it cannot be read, once the
 * counted steps are done and after the person closes it, it is not drawn, and
 * a failed read is not worth an alert on the first screen.
 */
export function SetupChecklist() {
    const t = useMessages();
    const canOpen = useCanOpen();
    const setup = useSetup();
    const dismiss = useDismissSetup();

    const data = setup.data;
    if (!data || data.dismissed || data.done >= data.total) return null;

    async function close() {
        try {
            await dismiss.mutateAsync();
        } catch {
            // Rendered from the mutation's own error state in the card.
        }
    }

    return (
        <Card
            title={t.dashboard.setup.title}
            description={t.dashboard.setup.progress(data.done, data.total)}
            action={
                <Button variant="ghost" loading={dismiss.isPending} onClick={close}>
                    {t.common.close}
                </Button>
            }
        >
            <div className="flex flex-col gap-4">
                {dismiss.isError && <Alert tone="danger">{problemText(t, dismiss.error)}</Alert>}
                <List>
                    {data.steps.map((step) => {
                        const words = t.dashboard.setup.steps[step.key];
                        const to = STEP_PAGES[step.key];
                        return (
                            <ListRow
                                key={step.key}
                                title={
                                    canOpen(to) ? (
                                        <Link to={to} className="inline-flex min-h-11 items-center font-medium hover:text-primary-strong">
                                            {words.title}
                                        </Link>
                                    ) : (
                                        <span className="inline-flex min-h-11 items-center font-medium">{words.title}</span>
                                    )
                                }
                                aside={
                                    step.done ? (
                                        <Badge tone="success">{t.dashboard.setup.done}</Badge>
                                    ) : step.optional ? (
                                        <Badge>{t.dashboard.setup.optional}</Badge>
                                    ) : (
                                        <Badge tone="warning">{t.dashboard.setup.todo}</Badge>
                                    )
                                }
                            >
                                {words.hint}
                            </ListRow>
                        );
                    })}
                </List>
            </div>
        </Card>
    );
}
